import { supabase } from '@/lib/supabaseClient';
import type { GenerationStatus } from '@/types/history';
import type { GenerationDetail } from '@/types/generation';

import { getSignedImageUrls } from './storage';

interface RawImage {
  id: string;
  storage_path: string;
  width: number | null;
  height: number | null;
  position: number;
}

interface RawGenerationRow {
  id: string;
  collection_id: string;
  selections: Record<string, string[]>;
  status: GenerationStatus;
  provider: string | null;
  aspect_ratio_id: string;
  generation_images: RawImage[];
}

/**
 * «Не найдено» и «чужое» неотличимы намеренно — тот же принцип, что уже
 * применён в delete-generation: RLS для чужой записи и правда несуществующий
 * id возвращают одинаковый пустой результат, а не разные ошибки.
 */
export async function fetchGenerationResult(generationId: string): Promise<GenerationDetail | null> {
  if (!supabase) {
    return null;
  }

  const { data, error } = await supabase
    .from('generations')
    .select(
      'id, collection_id, selections, status, provider, aspect_ratio_id, generation_images(id, storage_path, width, height, position)',
    )
    .eq('id', generationId)
    .maybeSingle();

  if (error) {
    throw new Error('Не удалось загрузить результат.');
  }

  if (!data) {
    return null;
  }

  const row = data as unknown as RawGenerationRow;
  const images = [...row.generation_images].sort((a, b) => a.position - b.position);
  const signedUrls = await getSignedImageUrls(images.map((image) => image.storage_path));
  const favoriteStatus = await fetchFavoriteStatus(images.map((image) => image.id));

  return {
    id: row.id,
    status: row.status,
    collectionId: row.collection_id,
    selections: row.selections,
    aspectRatioId: row.aspect_ratio_id,
    provider: row.provider ?? null,
    images: images.map((image) => ({
      id: image.id,
      path: image.storage_path,
      signedUrl: signedUrls.get(image.storage_path) ?? null,
      width: image.width,
      height: image.height,
      isFavorite: favoriteStatus.get(image.id)?.isFavorite ?? false,
      favoriteId: favoriteStatus.get(image.id)?.favoriteId ?? null,
    })),
  };
}

async function fetchFavoriteStatus(
  imageIds: string[],
): Promise<Map<string, { isFavorite: boolean; favoriteId: string }>> {
  const result = new Map<string, { isFavorite: boolean; favoriteId: string }>();

  if (!supabase || imageIds.length === 0) {
    return result;
  }

  const { data, error } = await supabase
    .from('favorites')
    .select('id, generation_image_id')
    .in('generation_image_id', imageIds);

  if (error || !data) {
    return result;
  }

  for (const row of data) {
    result.set(row.generation_image_id, { isFavorite: true, favoriteId: row.id });
  }

  return result;
}
